import React, {useMemo} from 'react'
import ContainerLayout from '../../../../components/layout/ContainerLayout'
import Text from '../../../../components/text/Text'
import {ICopierDetail} from '../../../../apis/copier/copierDetail'
import {ICopierRow} from '../../../../apis/copier/copiers'
import {useDisplayNumber} from '../../../../utils/number'
import {useColor} from '../../../../utils/etc'
import useQueryExchanges from '../../../../hooks/etc/useQueryExchanges'
import WizImage from '../../../../components/image/WizImage'

interface IProps {
    className?: string
    data?: ICopierDetail
    botRow?: ICopierRow // 목록에서 찾아온 같은 봇의 행 — 디테일에 없는 수치를 채운다
}

// 라벨을 붙여 보여줄 설정 키. 그 외 키는 아래에서 자동으로 나열한다.
const KNOWN_SETTINGS: {key: string; label: string; type: 'percent' | 'number' | 'bool'}[] = [
    {key: 'take_profit', label: 'Take Profit', type: 'percent'},
    {key: 'stop_loss', label: 'Stop Loss', type: 'percent'},
    {key: 'leverage', label: 'Leverage', type: 'number'},
    {key: 'max_leverage', label: 'Max Leverage', type: 'number'},
    {key: 'fixed_balance', label: 'Fixed Balance', type: 'number'},
    {key: 'mirror', label: 'Mirror Mode', type: 'bool'},
    {key: 'inverse', label: 'Inverse', type: 'bool'},
    {key: 'is_compound', label: 'Compound', type: 'bool'},
    {key: 'start_clean', label: 'Start Clean', type: 'bool'},
    {key: 'force_multiple', label: 'Force Multiple', type: 'bool'},
]

const CopyTradingInfoContainer: React.FC<IProps> = ({className, data, botRow}) => {
    const {renderPercent, floorBalance} = useDisplayNumber()
    const {getNumberColor} = useColor()

    const {data: ExchangeData} = useQueryExchanges()

    const bot = botRow?.bot
    const settings = data?.settings

    // 화이트리스트에 없는 설정 키 — 봇 설정 JSON이 그대로 실려 오므로 놓치지 않게 따로 나열한다.
    const extraSettings = useMemo(() => {
        const known = KNOWN_SETTINGS.map(item => item.key)
        return Object.entries(settings ?? {}).filter(([key]) => !known.includes(key))
    }, [settings])

    const renderSetting = (key: string, type: 'percent' | 'number' | 'bool') => {
        const value = settings?.[key]
        if (value === undefined || value === null || value === '') {
            return '-'
        }
        if (type === 'bool') {
            return value === true ? 'Y' : 'N'
        }
        if (type === 'percent') {
            return renderPercent(Number(value))
        }
        return String(value)
    }

    const cell = (label: string, value: React.ReactNode) => (
        <div className={'text-center space-y-[5px]'}>
            <Text className={'text-ti3 text-font03'}>{label}</Text>
            {value}
        </div>
    )

    return (
        <div className={`space-y-[15px] ${className}`}>
            <ContainerLayout
                title={'Copy Trading Info'}
                containerClassName={'grid grid-cols-6 gap-y-[20px] lg:grid-cols-3 sm:grid-cols-2'}>
                {cell(
                    'Exchange',
                    <div className={'flex items-center space-x-[5px] justify-center'}>
                        <Text className={'text-ti1'}>
                            {ExchangeData?.find(item => item?.name === data?.exchange)?.display_name}
                        </Text>
                        <WizImage
                            src={ExchangeData?.find(item => item?.name === data?.exchange)?.icon}
                            className={'w-[20px]'}
                        />
                    </div>,
                )}
                {cell(
                    'Start Balance',
                    <Text className={'text-ti2'}>
                        {bot ? floorBalance(bot?.initial_balance) : '-'}
                    </Text>,
                )}
                {cell(
                    'Withdraw / Deposit',
                    <Text className={'text-ti2'}>
                        {bot ? `${floorBalance(bot?.withdraw)} / ${floorBalance(bot?.deposit)}` : '-'}
                    </Text>,
                )}
                {cell(
                    'Current Balance',
                    <div>
                        <Text className={`text-ti2 ${getNumberColor(bot?.earning ?? 0)}`}>
                            {floorBalance(data?.current_balance ?? 0)}
                        </Text>
                        {bot && (
                            <Text className={`text-body3 ${getNumberColor(bot?.earning)}`}>
                                ({bot?.earning > 0 ? '+' : ''}
                                {floorBalance(bot?.earning)})
                            </Text>
                        )}
                    </div>,
                )}
                {cell(
                    'Profit',
                    <Text className={`text-ti2 ${getNumberColor(bot?.profit ?? 0)}`}>
                        {bot ? renderPercent(bot?.profit) : '-'}
                    </Text>,
                )}
                {cell(
                    'Loss Recovery',
                    <Text className={`text-ti2 ${data?.owed_earning ? 'text-font_red' : ''}`}>
                        {data?.owed_earning ? floorBalance(data?.owed_earning) : '-'}
                    </Text>,
                )}
            </ContainerLayout>

            <ContainerLayout
                title={'Bot Settings'}
                containerClassName={'grid grid-cols-5 gap-y-[20px] lg:grid-cols-3 sm:grid-cols-2'}>
                {KNOWN_SETTINGS.map(item =>
                    cell(item.label, <Text className={'text-ti2'}>{renderSetting(item.key, item.type)}</Text>),
                )}
                {extraSettings.map(([key, value]) =>
                    cell(key, <Text className={'text-ti2 break-all'}>{JSON.stringify(value)}</Text>),
                )}
            </ContainerLayout>
        </div>
    )
}

export default CopyTradingInfoContainer
